"use client";

import { useState, useRef, useEffect } from "react";
import { ArrowUp, Loader2, Paperclip, Plus, Mic, Square, X, FileText, Image as ImageIcon, MicOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { TermsAgreement } from "./input/TermsAgreement";
import { TranscriptionTypingAnimation } from "./input/TranscriptionTypingAnimation";
import { AudioVisualizerBars } from "./input/AudioVisualizerBars";
import { Attachment, chatAPI } from "@/lib/api";
import { useAudioRecording } from "@/hooks/useAudioRecording";
import { useIsMobile } from "@/hooks/useMediaQuery";

interface ChatInputContainerProps {
  onSendMessage: (message: string, attachments?: Attachment[], metadata?: { is_from_stt?: boolean }) => void;
  onStopStreaming?: () => void;
  disabled: boolean;
  isStreaming?: boolean;
  showTerms: boolean;
  onAgreeToTerms: () => void;
}

/**
 * ChatInputContainer - The fixed bottom input area with integrated chat input
 * This component handles:
 * - Fixed positioning at viewport bottom
 * - Terms agreement overlay
 * - Chat input field with textarea and buttons
 * - Message submission and validation
 * - Completely independent of message scrolling
 */
export function ChatInputContainer({
  onSendMessage,
  onStopStreaming,
  disabled,
  isStreaming = false,
  showTerms,
  onAgreeToTerms,
}: ChatInputContainerProps) {
  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<Attachment[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isFromSTT, setIsFromSTT] = useState(false);  // Track if message is from speech-to-text
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Detect mobile devices
  const isMobile = useIsMobile();

  // Audio recording hook
  const {
    recordingState,
    startRecording,
    stopRecording,
    error: recordingError,
    isSupported: isAudioSupported,
    audioLevels,
  } = useAudioRecording();

  // Log recording errors
  useEffect(() => {
    if (recordingError) {
      console.error('Recording error:', recordingError);
      setFileError(recordingError);
    }
  }, [recordingError]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 120) + "px";
    }
  }, [message]);

  // File handling functions
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setFileError(null);

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB per file
    const maxFiles = 5; // Maximum number of files

    // Check if adding these files would exceed the limit
    if (selectedFiles.length + files.length > maxFiles) {
      setFileError(`Maximal ${maxFiles} Dateien erlaubt`);
      return;
    }

    // Validate all files first
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      if (!allowedTypes.includes(file.type)) {
        setFileError(`${file.name}: Nur Bilder (PNG, JPG, WEBP) und PDFs sind erlaubt`);
        return;
      }
      if (file.size > maxSize) {
        setFileError(`${file.name}: Datei ist zu groß. Maximum: 5MB`);
        return;
      }
    }

    // Convert all files to base64 in parallel
    try {
      const attachmentPromises = fileArray.map((file) => {
        return new Promise<Attachment>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result as string;
            // Remove the data URL prefix (e.g., "data:image/png;base64,")
            const base64Data = base64.split(',')[1];

            const attachment: Attachment = {
              type: file.type.startsWith('image/') ? 'image' : 'pdf',
              mime_type: file.type,
              data: base64Data,
              filename: file.name,
              size: file.size,
            };

            resolve(attachment);
          };

          reader.onerror = () => {
            reject(new Error(`Fehler beim Lesen von ${file.name}`));
          };

          reader.readAsDataURL(file);
        });
      });

      const newAttachments = await Promise.all(attachmentPromises);
      setSelectedFiles((prev) => [...prev, ...newAttachments]);
    } catch (error) {
      setFileError(error instanceof Error ? error.message : 'Fehler beim Verarbeiten der Dateien');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  };

  // Handle microphone button click (toggle recording)
  const handleMicrophoneClick = async () => {
    // Clear any previous errors
    setFileError(null);

    if (recordingState === 'idle') {
      // Start recording
      console.log('Starting audio recording');
      await startRecording();
    } else if (recordingState === 'recording') {
      // Stop recording and transcribe
      try {
        setIsTranscribing(true);
        console.log('Stopping recording and transcribing...');

        const audioBlob = await stopRecording();

        if (audioBlob) {
          // Transcribe the audio
          const transcribedText = await chatAPI.transcribeAudio(audioBlob);
          console.log('Transcription successful:', transcribedText);

          // Add transcribed text to the message input
          setMessage((prevMessage) => {
            // If there's existing text, add a space before appending
            return prevMessage ? `${prevMessage} ${transcribedText}` : transcribedText;
          });

          // Mark message as coming from STT for analytics
          setIsFromSTT(true);

          // Focus on textarea so user can edit/send
          textareaRef.current?.focus();
        }
      } catch (error) {
        console.error('Transcription error:', error);
        setFileError(
          error instanceof Error
            ? error.message
            : 'Fehler bei der Transkription. Bitte versuchen Sie es erneut.'
        );
      } finally {
        setIsTranscribing(false);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // If streaming, handle stop action
    if (isStreaming && onStopStreaming) {
      onStopStreaming();
      return;
    }

    // Otherwise, send message as normal
    // Allow sending if there's either a message or files
    if ((message.trim() || selectedFiles.length > 0) && !disabled) {
      // Pass metadata about STT usage for Langfuse tracking
      onSendMessage(
        message.trim() || "Here's a document",
        selectedFiles.length > 0 ? selectedFiles : undefined,
        { is_from_stt: isFromSTT }
      );
      setMessage("");
      setSelectedFiles([]);
      setFileError(null);
      setIsFromSTT(false);  // Reset STT flag after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't allow Enter to stop streaming - only send messages
    // On mobile, allow Enter to add new lines (users can send via button)
    if (e.key === "Enter" && !e.shiftKey && !isStreaming && !isMobile) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  return (
    <div className="relative">
      {/* Terms Agreement - only show if user hasn't sent first message */}
      <AnimatePresence>
        {showTerms && <TermsAgreement onAgree={onAgreeToTerms} />}
      </AnimatePresence>

      {/* Outer wrapper with centered content */}
      <div className="px-2 pb-0 lg:px-4 lg:pt-2">
        <div className="max-w-3xl mx-auto">
          {/* Glassmorphic container with gradient border effect */}
          <div
            className={cn(
              "relative rounded-t-2xl", // Round top corners on all screen sizes
              "overflow-hidden",
              "bg-[rgba(248,246,255,0.5)]",
              "outline-solid outline-8 outline-[rgba(109,74,255,0.08)]"
            )}
            style={{
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid rgba(248, 246, 255, 0.7)",
              borderBottom: "none", // Remove bottom border since it connects to bottom
              boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
              transform: "translateZ(0)", // Force GPU acceleration for Safari
              willChange: "transform", // Optimize for Safari
            }}
          >
            <form onSubmit={handleSubmit} className="relative w-full px-3 pt-3">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
                onChange={handleFileSelect}
                multiple
                className="hidden"
              />

              {/* File preview area */}
              {selectedFiles.length > 0 && (
                <div className="pb-2 space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={`${file.filename}-${index}`}
                      className="flex items-center gap-2 p-2 bg-[#6D4AFF]/10 rounded-lg border border-[#6D4AFF]/20"
                    >
                      {file.type === 'image' ? (
                        <ImageIcon className="h-5 w-5 text-[#6D4AFF] shrink-0" />
                      ) : (
                        <FileText className="h-5 w-5 text-[#6D4AFF] shrink-0" />
                      )}
                      <span className="text-sm text-gray-700 flex-1 truncate">
                        {file.filename}
                      </span>
                      <span className="text-xs text-gray-500 shrink-0">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="p-1 hover:bg-[#6D4AFF]/20 rounded-sm transition-colors shrink-0"
                      >
                        <X className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Error message */}
              {fileError && (
                <div className="pb-2">
                  <div className="p-2 bg-red-50 rounded-lg border border-red-200">
                    <span className="text-sm text-red-600">{fileError}</span>
                  </div>
                </div>
              )}

              {/* Transcription typing animation */}
              {isTranscribing && (
                <div className="pb-2">
                  <div className="p-3 bg-[#6D4AFF]/5 rounded-lg border border-[#6D4AFF]/20">
                    <TranscriptionTypingAnimation />
                  </div>
                </div>
              )}

              {/* Top level: Text input area */}
              <div className="pb-1">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Nachricht hier eingeben..."
                  disabled={disabled}
                  rows={1}
                  className={cn(
                    "w-full resize-none",
                    "bg-transparent",
                    "text-gray-700 ",
                    "placeholder:text-gray-400 ",
                    "text-base leading-relaxed", // Changed from text-sm to text-base (16px) to prevent zoom
                    "focus:outline-hidden focus:ring-0 focus:border-transparent",
                    "border-none outline-hidden",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    "transition-all duration-200",
                    "overflow-y-auto",
                    message.length > 100 ? "scrollbar-thin" : "overflow-hidden"
                  )}
                  style={{
                    minHeight: "32px",
                    outline: "none",
                    fontSize: "16px", // Ensure 16px minimum to prevent iOS zoom
                  }}
                />
              </div>

              {/* Bottom level: Action buttons */}
              <div className="flex items-center justify-between pt-1 pb-2">
                {/* Left side: Attachment button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || isStreaming}
                  className={cn(
                    "p-2 rounded-xl relative",
                    "text-gray-400 hover:text-[#6D4AFF]",
                    "hover:bg-[#6D4AFF]/5",
                    "transition-all duration-200",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    selectedFiles.length > 0 && "text-[#6D4AFF] bg-[#6D4AFF]/5"
                  )}
                >
                  <Plus className="h-5 w-5 md:hidden" />
                  <Paperclip className="h-5 w-5 hidden md:block" />
                  {selectedFiles.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-[#6D4AFF] text-white text-xs rounded-full flex items-center justify-center">
                      {selectedFiles.length}
                    </span>
                  )}
                </motion.button>

                {/* Right side: Microphone and Send buttons */}
                <div className="flex items-center gap-1">
                  {/* Microphone button with audio visualizer */}
                  {isAudioSupported && (
                    <div className="relative">
                      {/* Audio visualizer bars around the button */}
                      <AudioVisualizerBars
                        audioLevels={audioLevels}
                        isActive={recordingState === 'recording'}
                      />
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={handleMicrophoneClick}
                        disabled={disabled || isStreaming || isTranscribing}
                        className={cn(
                          "p-1.5 rounded-full relative",
                          "transition-all duration-200",
                          "disabled:opacity-50 disabled:cursor-not-allowed",
                          recordingState === 'recording'
                            ? "text-red-500 bg-red-50 hover:bg-red-100"
                            : isTranscribing
                              ? "text-[#6D4AFF] bg-[#6D4AFF]/10"
                              : "text-gray-400 hover:text-red-500 hover:bg-red-50"
                        )}
                      >
                        {isTranscribing ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : recordingState === 'recording' ? (
                          <MicOff className="h-5 w-5" />
                        ) : (
                          <Mic className="h-5 w-5" />
                        )}
                      </motion.button>
                    </div>
                  )}

                  {/* Send/Stop button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    disabled={!isStreaming && ((!message.trim() && selectedFiles.length === 0) || disabled)}
                    className={cn(
                      "p-2 rounded-full",
                      "transition-all duration-200",
                      isStreaming
                        ? "bg-[#6D4AFF] text-white hover:bg-[#5936D9] shadow-xs" // Purple for stop
                        : (message.trim() || selectedFiles.length > 0) && !disabled
                          ? "bg-[#6D4AFF] text-white hover:bg-[#5936D9] shadow-xs" // Purple for send
                          : "bg-gray-100 text-gray-400 hover:bg-gray-200 ", // Gray when disabled
                      "disabled:pointer-events-none disabled:opacity-30"
                    )}
                  >
                    {isStreaming ? (
                      <Square className="h-5 w-5" /> // Stop icon when streaming
                    ) : disabled ? (
                      <Loader2 className="h-5 w-5 animate-spin" /> // Loading when processing but not streaming
                    ) : (
                      <ArrowUp className="h-5 w-5" /> // Arrow when ready to send
                    )}
                  </motion.button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
